import React, { useMemo, useState } from "react";
import SettingsInfoTip from "./shared/SettingsInfoTip";
import {
  HOUR_OPTIONS,
  MEETING_PREF_PRESET_CUSTOM,
  MEETING_PREF_PRESETS,
  resolveMeetingPrefPreset,
} from "./shared/meetingPreferences";

/**
 * Optional meeting preference picker for when AI suggests calendar times.
 *
 * @example
 * <ActionItemsSettingsMeetingPrefsSection
 *   variant="web"
 *   workingHoursStart={9}
 *   workingHoursEnd={12}
 *   onChange={({ workingHoursStart, workingHoursEnd }) => save(...)}
 * />
 */
const ActionItemsSettingsMeetingPrefsSection = ({
  variant = "web",
  workingHoursStart = null,
  workingHoursEnd = null,
  onChange,
}) => {
  const isWeb = variant === "web";
  const resolvedPreset = resolveMeetingPrefPreset(workingHoursStart, workingHoursEnd);
  const [customStart, setCustomStart] = useState(
    workingHoursStart != null ? Number(workingHoursStart) : 9
  );
  const [customEnd, setCustomEnd] = useState(
    workingHoursEnd != null ? Number(workingHoursEnd) : 18
  );

  const endOptions = useMemo(
    () => HOUR_OPTIONS.filter((option) => option.value > customStart),
    [customStart]
  );

  const handlePresetClick = (presetId) => {
    const preset = MEETING_PREF_PRESETS.find((item) => item.id === presetId);
    if (!preset) return;
    if (presetId === MEETING_PREF_PRESET_CUSTOM) {
      const start = customStart;
      const end = customEnd > start ? customEnd : Math.min(start + 3, 23);
      setCustomEnd(end);
      onChange?.({ workingHoursStart: start, workingHoursEnd: end });
      return;
    }
    onChange?.({
      workingHoursStart: preset.start,
      workingHoursEnd: preset.end,
    });
  };

  const handleCustomStartChange = (value) => {
    const start = Number(value);
    const end = customEnd > start ? customEnd : Math.min(start + 3, 23);
    setCustomStart(start);
    setCustomEnd(end);
    onChange?.({ workingHoursStart: start, workingHoursEnd: end });
  };

  const handleCustomEndChange = (value) => {
    const end = Number(value);
    setCustomEnd(end);
    onChange?.({ workingHoursStart: customStart, workingHoursEnd: end });
  };

  const picker = (
    <>
      <div className="settings-style-toggle" role="group" aria-label="When should AI suggest meetings">
        {MEETING_PREF_PRESETS.map((option) => {
          const active = resolvedPreset === option.id;
          return (
            <button
              key={option.id}
              type="button"
              className={`settings-style-btn${active ? " settings-style-btn--active" : ""}`}
              aria-pressed={active}
              title={option.description}
              onClick={() => handlePresetClick(option.id)}
            >
              {option.chipLabel || option.label}
            </button>
          );
        })}
      </div>
      {resolvedPreset === MEETING_PREF_PRESET_CUSTOM ? (
        <div className="settings-meeting-hours">
          <label className="settings-meeting-hours__field">
            <span className="settings-meeting-hours__label">From</span>
            <select
              className="settings-meeting-hours__select"
              value={customStart}
              onChange={(event) => handleCustomStartChange(event.target.value)}
            >
              {HOUR_OPTIONS.slice(0, 23).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="settings-meeting-hours__field">
            <span className="settings-meeting-hours__label">To</span>
            <select
              className="settings-meeting-hours__select"
              value={customEnd}
              onChange={(event) => handleCustomEndChange(event.target.value)}
            >
              {(endOptions.length ? endOptions : HOUR_OPTIONS.slice(1)).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
    </>
  );

  if (isWeb) {
    return (
      <div className="settings-general-block settings-general-block--nested">
        <div className="settings-general-block__heading-row">
          <h4 className="settings-general-block__subtitle">When should AI suggest meetings?</h4>
          <SettingsInfoTip
            label="About meeting preferences"
            text="Optional. Used only to prefill suggested times — never auto-books. Explicit times on a task still win. Defaults to 9am–6pm."
          />
        </div>
        {picker}
      </div>
    );
  }

  return (
    <div className="settings-general-piece">
      <p className="settings-general-piece__desc">
        Optional hours for suggested meeting times. Explicit times on a task still win.
      </p>
      <div className="settings-general-piece__heading-row">
        <span className="settings-general-piece__label">Meeting suggestions</span>
        <SettingsInfoTip
          label="About meeting preferences"
          text="Prefills suggested times only — never auto-books. Uses 9am–6pm until you customize."
        />
      </div>
      {picker}
    </div>
  );
};

export default ActionItemsSettingsMeetingPrefsSection;
